import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Ban, UserCheck, Mail, Phone, Calendar, Wallet, Trash2, AlertTriangle, Shield, DollarSign, Eye, EyeOff, Lock, Bot } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  // Delete user modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  // Wipe all data modal
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [wipePassword, setWipePassword] = useState('');
  const [wipeLoading, setWipeLoading] = useState(false);
  // Trade credit editing
  const [editingCredit, setEditingCredit] = useState(null);
  const [creditInput, setCreditInput] = useState('');
  // Spread editing
  const [editingSpread, setEditingSpread] = useState(null);
  const [spreadInput, setSpreadInput] = useState('');
  // Password visibility
  const [visiblePasswords, setVisiblePasswords] = useState({});
  // Robo trading controls
  const [roboGlobal, setRoboGlobal] = useState(true);

  const loadUsers = () => {
    adminAPI.getUsers().then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
    adminAPI.getRoboSettings().then(r => setRoboGlobal(r.data.global_enabled)).catch(() => {});
  };
  useEffect(() => { loadUsers(); }, []);

  const handleRoboGlobal = async (enabled) => {
    try {
      const { data } = await adminAPI.setRoboGlobal(enabled);
      setRoboGlobal(data.global_enabled);
      toast.success(enabled ? 'Robo trading enabled platform-wide'
        : `Robo trading disabled platform-wide (${data.bots_stopped} bot(s) stopped)`);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleUserRobo = async (userId, enabled) => {
    setActionLoading(userId);
    try {
      await adminAPI.setUserRobo(userId, enabled);
      toast.success(`Robo trading ${enabled ? 'enabled' : 'disabled'} for user ${userId}`);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleStatus = async (userId, status) => {
    setActionLoading(userId);
    try {
      await adminAPI.updateUserStatus(userId, status);
      toast.success(`User ${status === 'active' ? 'activated' : 'blocked'}`);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    finally { setActionLoading(null); }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText !== deleteTarget.user_id) {
      toast.error('Please type the User ID to confirm'); return;
    }
    setActionLoading(deleteTarget.user_id);
    try {
      await adminAPI.deleteUser(deleteTarget.user_id);
      toast.success(`User ${deleteTarget.user_id} (${deleteTarget.name}) deleted successfully`);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally { setActionLoading(null); }
  };

  const handleSetTradeCredit = async (userId) => {
    const credit = parseFloat(creditInput);
    if (isNaN(credit) || credit < 0) { toast.error('Enter a valid credit amount'); return; }
    setActionLoading(userId);
    try {
      await adminAPI.setTradeCredit(userId, credit);
      toast.success(`Trade credit set to ${formatCurrency(credit)}`);
      setEditingCredit(null);
      setCreditInput('');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleSetSpread = async (userId) => {
    const sp = parseFloat(spreadInput);
    if (isNaN(sp) || sp < 0) { toast.error('Enter a valid spread %'); return; }
    setActionLoading(userId);
    try {
      await adminAPI.setUserSpread(userId, sp);
      toast.success(`Spread set to ${sp}%`);
      setEditingSpread(null);
      setSpreadInput('');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const togglePassword = async (userId) => {
    if (visiblePasswords[userId]) {
      setVisiblePasswords(p => { const n = { ...p }; delete n[userId]; return n; });
    } else {
      try {
        const { data } = await adminAPI.getUserPassword(userId);
        setVisiblePasswords(p => ({ ...p, [userId]: data.password }));
      } catch { toast.error('Failed to fetch password'); }
    }
  };

  const handleWipeAllData = async () => {
    if (!wipePassword) { toast.error('Enter admin password'); return; }
    setWipeLoading(true);
    try {
      const { data } = await adminAPI.wipeAllData(wipePassword);
      toast.success(data.message || 'All data deleted');
      setShowWipeModal(false);
      setWipePassword('');
      // Force logout — admin account was re-seeded
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wipe failed');
    } finally { setWipeLoading(false); }
  };

  const filtered = users
    .filter(u => filter === 'all' || u.status === filter || (filter === 'admin' && u.role === 'admin') || (filter === 'demo' && u.account_type === 'demo'))
    .filter(u => !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()) || u.user_id?.includes(search));

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-users-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">User Management</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{users.length} total users</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or ID..." data-testid="admin-user-search" className="pl-9 bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] h-10" />
            </div>
            <button
              onClick={() => handleRoboGlobal(!roboGlobal)}
              data-testid="admin-robo-global-toggle"
              className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5 ${roboGlobal
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                : 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20'}`}
            >
              <Bot className="w-3.5 h-3.5" />
              Robo: {roboGlobal ? 'ON (all users)' : 'OFF (kill switch)'}
            </button>
            <button
              onClick={() => setShowWipeModal(true)}
              data-testid="admin-wipe-all-btn"
              className="shrink-0 px-4 py-2 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-colors flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Wipe All Data
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all', label: `All (${users.length})` },
            { key: 'active', label: `Active (${users.filter(u => u.status === 'active').length})` },
            { key: 'blocked', label: `Blocked (${users.filter(u => u.status === 'blocked').length})` },
            { key: 'demo', label: `Demo (${users.filter(u => u.account_type === 'demo').length})` },
            { key: 'admin', label: `Admins (${users.filter(u => u.role === 'admin').length})` },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`admin-filter-${f.key}`}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.key ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'text-[var(--text-secondary)] bg-[var(--bg-surface)] border border-[var(--border-subtle)] hover:text-[var(--text-primary)]'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* User Cards */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--text-secondary)] bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">No users found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(u => (
              <div key={u.user_id} className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg hover:border-green-500/20 transition-all duration-200" data-testid={`admin-user-card-${u.user_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-green-500/15 flex items-center justify-center">
                      <span className="text-lg font-bold text-green-400">{u.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[var(--text-primary)]">{u.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${u.role === 'admin' ? 'bg-green-500/15 text-green-400' : 'bg-[var(--hover-bg)] text-[var(--text-secondary)]'}`}>{u.role}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${u.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{u.status}</span>
                        {u.account_type === 'demo' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400" data-testid={`demo-tag-${u.user_id}`}>DEMO</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Mail className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> {u.email}
                  </div>
                  {u.user_id && (
                    <div className="flex items-center gap-2 text-green-400">
                      <span className="w-3.5 h-3.5 text-[10px] font-bold text-center">#</span>
                      <span className="font-mono font-semibold" data-testid={`admin-user-id-${u.user_id}`}>ID: {u.user_id}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Phone className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> {u.phone || '-'}
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Lock className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> Password:
                    <span className="font-mono text-[var(--text-primary)]" data-testid={`password-value-${u.user_id}`}>
                      {visiblePasswords[u.user_id] || '••••••••'}
                    </span>
                    <button onClick={() => togglePassword(u.user_id)} data-testid={`password-toggle-${u.user_id}`}
                      className="p-0.5 rounded hover:bg-[var(--hover-bg)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                      {visiblePasswords[u.user_id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Wallet className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> Balance: <span className="font-mono text-[var(--text-primary)]">{formatCurrency(u.wallet_balance)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <DollarSign className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> Trade Credit:
                    {editingCredit === u.user_id ? (
                      <span className="flex items-center gap-1">
                        <input type="number" step="any" value={creditInput}
                          onChange={(e) => setCreditInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSetTradeCredit(u.user_id)}
                          data-testid={`trade-credit-input-${u.user_id}`}
                          className="w-24 h-6 px-1.5 text-xs font-mono bg-[var(--bg-primary)] border border-green-500/30 rounded text-[var(--text-primary)] focus:outline-none"
                          autoFocus />
                        <button onClick={() => handleSetTradeCredit(u.user_id)} data-testid={`trade-credit-save-${u.user_id}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30">Set</button>
                        <button onClick={() => setEditingCredit(null)} className="text-[10px] text-[var(--text-tertiary)]">×</button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <span className="font-mono text-green-400" data-testid={`trade-credit-value-${u.user_id}`}>{u.admin_trade_credit ? formatCurrency(u.admin_trade_credit) : 'Not set'}</span>
                        <button onClick={() => { setEditingCredit(u.user_id); setCreditInput(String(u.admin_trade_credit || '')); }} data-testid={`trade-credit-edit-${u.user_id}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--hover-bg)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Edit</button>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <DollarSign className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> Spread %:
                    {editingSpread === u.user_id ? (
                      <span className="flex items-center gap-1">
                        <input type="number" step="0.1" value={spreadInput}
                          onChange={(e) => setSpreadInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSetSpread(u.user_id)}
                          data-testid={`spread-input-${u.user_id}`}
                          className="w-20 h-6 px-1.5 text-xs font-mono bg-[var(--bg-primary)] border border-amber-500/30 rounded text-[var(--text-primary)] focus:outline-none"
                          autoFocus />
                        <button onClick={() => handleSetSpread(u.user_id)} data-testid={`spread-save-${u.user_id}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30">Set</button>
                        <button onClick={() => setEditingSpread(null)} className="text-[10px] text-[var(--text-tertiary)]">×</button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <span className="font-mono text-amber-400" data-testid={`spread-value-${u.user_id}`}>{u.default_spread_percent ? `${u.default_spread_percent}%` : 'Not set'}</span>
                        <button onClick={() => { setEditingSpread(u.user_id); setSpreadInput(String(u.default_spread_percent || '')); }} data-testid={`spread-edit-${u.user_id}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--hover-bg)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Edit</button>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Bot className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> Robo Trading:
                    <button
                      onClick={() => handleUserRobo(u.user_id, !(u.robo_enabled !== false))}
                      disabled={actionLoading === u.user_id}
                      data-testid={`robo-toggle-${u.user_id}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-colors disabled:opacity-50 ${u.robo_enabled !== false
                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25 hover:bg-emerald-500/25'
                        : 'bg-red-500/15 text-red-400 border-red-500/25 hover:bg-red-500/25'}`}
                    >
                      {u.robo_enabled !== false ? 'ALLOWED — click to disable' : 'BLOCKED — click to allow'}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Calendar className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> Joined: {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Shield className="w-3.5 h-3.5 text-[var(--text-tertiary)]" /> Withdrawal Restrictions:
                    <select value={u.withdrawal_restrictions || 'both'} data-testid={`restriction-mode-${u.user_id}`}
                      onChange={async (e) => {
                        try {
                          await adminAPI.setWithdrawalRestrictions(u.user_id, e.target.value);
                          toast.success(`Restriction mode set to: ${e.target.value}`);
                          loadUsers();
                        } catch { toast.error('Failed'); }
                      }}
                      className="h-6 px-2 text-[10px] font-medium bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-[var(--text-primary)]">
                      <option value="both">Both (Spread + Leverage)</option>
                      <option value="spread_only">Spread Only</option>
                      <option value="leverage_only">Leverage Only</option>
                      <option value="none">None (No Restrictions)</option>
                    </select>
                  </div>
                </div>

                {u.role !== 'admin' && (
                  <div className="mt-4 pt-3 border-t border-[var(--border-subtle)] flex gap-2">
                    <button
                      onClick={() => handleStatus(u.user_id, u.status === 'active' ? 'blocked' : 'active')}
                      disabled={actionLoading === u.user_id}
                      data-testid={`admin-toggle-user-${u.user_id}`}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 ${
                        u.status === 'active'
                          ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20'
                          : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20'
                      }`}
                    >
                      {u.status === 'active' ? <><Ban className="w-3.5 h-3.5" /> Block</> : <><UserCheck className="w-3.5 h-3.5" /> Activate</>}
                    </button>
                    <button
                      onClick={() => { setDeleteTarget(u); setDeleteConfirmText(''); }}
                      data-testid={`admin-delete-user-${u.user_id}`}
                      className="px-3 py-2 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-colors flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete User Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" data-testid="delete-user-modal">
          <div className="bg-[var(--bg-surface)] border border-red-500/30 rounded-lg p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--text-primary)] font-['Outfit']">Delete User</h3>
                <p className="text-xs text-[var(--text-secondary)]">This will permanently delete the user and ALL their data.</p>
              </div>
            </div>
            <div className="p-3 bg-red-500/5 border border-red-500/10 rounded text-xs text-[var(--text-secondary)]">
              <p><strong className="text-[var(--text-primary)]">{deleteTarget.name}</strong> ({deleteTarget.email})</p>
              <p className="mt-1">User ID: <span className="font-mono text-red-400 font-semibold">{deleteTarget.user_id}</span></p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)]">Type the User ID <span className="font-mono text-red-400">{deleteTarget.user_id}</span> to confirm:</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
                maxLength={6}
                placeholder={deleteTarget.user_id}
                data-testid="delete-user-confirm-input"
                className="bg-[var(--bg-primary)] border-red-500/20 text-[var(--text-primary)] font-mono text-center text-lg tracking-wider h-11 focus:border-red-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
                data-testid="delete-user-cancel-btn"
                className="flex-1 py-2.5 rounded-lg text-xs font-medium bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-subtle)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteConfirmText !== deleteTarget.user_id || actionLoading === deleteTarget.user_id}
                data-testid="delete-user-confirm-btn"
                className="flex-1 py-2.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wipe All Data Confirmation Modal */}
      {showWipeModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" data-testid="wipe-all-modal">
          <div className="bg-[var(--bg-surface)] border border-red-500/30 rounded-lg p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-red-400 font-['Outfit']">DANGER: Wipe All Data</h3>
                <p className="text-xs text-[var(--text-secondary)]">This will delete ALL users, trades, deposits, and system data.</p>
              </div>
            </div>
            <div className="p-3 bg-red-500/5 border border-red-500/10 rounded text-xs text-red-400">
              This action cannot be undone. The admin account will be re-seeded with default credentials. You will be logged out.
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)]">Enter your admin password to confirm:</Label>
              <Input
                type="password"
                value={wipePassword}
                onChange={(e) => setWipePassword(e.target.value)}
                placeholder="Admin password"
                data-testid="wipe-password-input"
                className="bg-[var(--bg-primary)] border-red-500/20 text-[var(--text-primary)] h-11 focus:border-red-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowWipeModal(false); setWipePassword(''); }}
                data-testid="wipe-cancel-btn"
                className="flex-1 py-2.5 rounded-lg text-xs font-medium bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-subtle)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleWipeAllData}
                disabled={!wipePassword || wipeLoading}
                data-testid="wipe-confirm-btn"
                className="flex-1 py-2.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
              >
                {wipeLoading ? 'Wiping...' : <><Trash2 className="w-3.5 h-3.5" /> Wipe Everything</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
